import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  Typography,
  Sheet,
  Select,
  Option,
  Box,
  Textarea,
} from "@mui/joy";

import toast from "react-hot-toast";
import axiosInstance from "../../api/axios";
import AuthContext from "../../contexts/authContext";

export default function AddDesign({
  openDesign,
  setOpenDesign,
  setAssetId,
  assetId,
  designId,
}) {
  const { setRefresh, userData } = useContext(AuthContext);
  const [editMode, setEditMode] = useState(false);
  const [designData, setDesignData] = useState({
    designName: "",
    assetType: "",
    location: "",
    amount: "",
    agent: "",
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 12))
      .toISOString()
      .split("T")[0],
    nextReminderDate: new Date(new Date().setMonth(new Date().getMonth() + 6))
      .toISOString()
      .split("T")[0],
    lat: "",
    long: "",
    size: "",
    dimensionUnit: "",
    description: "",
  });

  const assetTypeOptions = [
    "Banner",
    "Hoarding",
    "Digital Poster",
    "Flyer",
    "Wall Painting",
    "Flex Banner",
    "Standee",
    "Roll-Up Banner",
    "Backdrop Banner",
    "Pole Board",
    "Shop Board / Signage",
    "Theatres",
    "Vehicle Branding",
    "Glass Sticker",
    "Floor Sticker",
    "Canvas Print",
    "Directional Board",
  ];

  const agentOptions = userData?.users?.filter((u) => u.role === "agent") || [];

  // ----------------------------------------
  // Handle Input Changes
  // ----------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    // For Latitude & Longitude — allow only float numbers
    if (name === "lat" || name === "long") {
      // allow empty
      if (value === "") {
        setDesignData((p) => ({ ...p, [name]: "" }));
        return;
      }

      // allow only valid float pattern
      const floatRegex = /^-?\d*\.?\d*$/;
      if (!floatRegex.test(value)) return; // block invalid input
    }

    setDesignData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setDesignData((prev) => ({ ...prev, [name]: value }));
  };

  // ----------------------------------------
  // Validation
  // ----------------------------------------
  const validate = () => {
    const requiredFields = [
      "designName",
      "assetType",
      "location",
      "amount",
      "agent",
      "startDate",
      "endDate",
      "nextReminderDate",
      "size",
      "dimensionUnit",
    ];

    for (let field of requiredFields) {
      if (!designData[field] || designData[field]?.toString().trim() === "") {
        toast.error(`Please fill ${field} field.`);
        return false;
      }
    }

    return true;
  };

  // ----------------------------------------
  // Add Design
  // ----------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;
    try {
      const res = await axiosInstance.post("/designs/add", {
        ...designData,
        assetref: assetId,
      });

      if (res.status === 200) {
        toast.success("Design asset added successfully!");

        resetForm();
        setOpenDesign(false);
        setRefresh(true);
      }
    } catch (err) {
      console.log(err);
      toast.error("An error occurred while adding the design asset.");
    }
  };

  // ----------------------------------------
  // Update Design
  // ----------------------------------------
  const handleUpdateDesign = async (e) => {
    e.preventDefault();
    // Validate LATITUDE
    if (designData.lat !== "" && isNaN(Number(designData.lat))) {
      toast.error("Latitude must be a valid number.");
      return;
    }

    // Validate LONGITUDE
    if (designData.long !== "" && isNaN(Number(designData.long))) {
      toast.error("Longitude must be a valid number.");
      return;
    }
    if (!validate()) return;
    try {
      const res = await axiosInstance.post(`/designs/edit/${designId}`, {
        ...designData,
        assetref: assetId,
      });

      if (res.status === 200) {
        toast.success("Design asset updated successfully!");

        resetForm();
        setOpenDesign(false);
        setRefresh(true);
      }
    } catch (err) {
      toast.error("An error occurred while updating the design asset.");
    }
  };

  // ----------------------------------------
  // Reset Form
  // ----------------------------------------
  const resetForm = () => {
    setDesignData({
      designName: "",
      assetType: "",
      location: "",
      amount: "",
      agent: "",
      startDate: new Date().toISOString().split("T")[0],
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 12))
        .toISOString()
        .split("T")[0],
      nextReminderDate: new Date(new Date().setMonth(new Date().getMonth() + 6))
        .toISOString()
        .split("T")[0],
      lat: "",
      long: "",
      size: "",
      dimensionUnit: "",
      description: "",
    });

    setEditMode(false);
  };

  // ----------------------------------------
  // Load existing design (Edit Mode)
  // ----------------------------------------
  useEffect(() => {
    if (!designId) return;

    setEditMode(true);

    const asset = userData?.assets?.find((a) => a._id === assetId);
    const design = asset?.designs?.find((d) => d._id?.toString() === designId);

    if (design) {
      setDesignData({
        designName: design.designName,
        assetType: design.assetType,
        location: design.location,
        amount: design.amount,
        agent: design.agent?._id || "",
        startDate: new Date(design.startDate).toISOString().split("T")[0],
        endDate: new Date(design.endDate).toISOString().split("T")[0],
        nextReminderDate: new Date(design.nextReminderDate)
          .toISOString()
          .split("T")[0],
        lat: design.lat || "",
        long: design.long || "",
        size: design.size || "",
        dimensionUnit: design.dimensionUnit || "",
        description: design.description || "",
      });
    }
  }, [assetId, designId, userData]);

  return (
    <Modal
      open={openDesign}
      onClose={() => setOpenDesign(false)}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          width: "95%",
          maxWidth: 500,
          bgcolor: "#FEDDDD",
          p: 3,
          borderRadius: "md",
          boxShadow: "lg",
          height: "70%",
          overflowY: "auto",
          margin: "10px",
        }}
      >
        <ModalClose
          sx={{
            m: 1,
            "& svg": { color: "#800001" },
            "&:hover": {
              backgroundColor: "#800001",
              "& svg": { color: "#fff" },
            },
          }}
          onClick={() => setOpenDesign(false)}
        />

        <Typography
          level="h4"
          sx={{
            color: "#800001",
            fontWeight: "bold",
            mb: 2,
            textAlign: "center",
          }}
        >
          {editMode ? "Edit Design Asset" : "Add Design Asset"}
        </Typography>

        <Box
          component="form"
          onSubmit={editMode ? handleUpdateDesign : handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          {/* DESIGN NAME + ASSET TYPE */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Design Name</FormLabel>
              <Input
                name="designName"
                value={designData.designName}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Asset Type</FormLabel>
              <Select
                value={designData.assetType}
                onChange={(e, val) => handleSelectChange("assetType", val)}
              >
                {assetTypeOptions.map((type, idx) => (
                  <Option key={idx} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* LOCATION + AMOUNT */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Location</FormLabel>
              <Input
                name="location"
                value={designData.location}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Amount</FormLabel>
              <Input
                name="amount"
                type="number"
                value={designData.amount}
                onChange={handleChange}
              />
            </FormControl>
          </Box>

          {/* DATES */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Start Date</FormLabel>
              <Input
                name="startDate"
                type="date"
                value={designData.startDate}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <FormLabel>End/Renewal Date</FormLabel>
              <Input
                name="endDate"
                type="date"
                value={designData.endDate}
                onChange={handleChange}
              />
            </FormControl>
          </Box>

          {/* AGENT + NEXT REMINDER */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Agent</FormLabel>
              <Select
                value={designData.agent}
                onChange={(e, val) => handleSelectChange("agent", val)}
              >
                {agentOptions.map((agent) => (
                  <Option key={agent._id} value={agent._id}>
                    {agent.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Next Reminder Date</FormLabel>
              <Input
                name="nextReminderDate"
                type="date"
                value={designData.nextReminderDate}
                onChange={handleChange}
              />
            </FormControl>
          </Box>

          {/* LAT LONG */}
          {editMode && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Latitude</FormLabel>
                <Input
                  name="lat"
                  type="number"
                  value={designData.lat}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Longitude</FormLabel>
                <Input
                  name="long"
                  type="number"
                  value={designData.long}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>
          )}
          {/* SIZE + UNIT */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Size</FormLabel>
              <Input
                name="size"
                placeholder="40 × 20"
                value={designData.size}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Unit</FormLabel>
              <Select
                value={designData.dimensionUnit}
                onChange={(e, val) => handleSelectChange("dimensionUnit", val)}
              >
                <Option value="ft">Feet (ft)</Option>
                <Option value="in">Inches (in)</Option>
                <Option value="cm">Centimeter (cm)</Option>
                <Option value="mm">Millimeter (mm)</Option>
                <Option value="m">Meter (m)</Option>
                <Option value="min">Minute (min)</Option>
                <Option value="sec">Seconds (sec)</Option>
              </Select>
            </FormControl>
          </Box>

          {/* DESCRIPTION */}
          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              minRows={4}
              name="description"
              value={designData.description}
              onChange={handleChange}
              placeholder="Write description..."
            />
          </FormControl>

          {/* BUTTON */}
          <Button
            type="submit"
            fullWidth
            sx={{
              mt: 2,
              backgroundColor: "#800001",
              color: "white",
              "&:hover": { backgroundColor: "#800001" },
            }}
          >
            {editMode ? "Update" : "Submit"}
          </Button>
        </Box>
      </Sheet>
    </Modal>
  );
}
